/**
 * Paraderos físicos: las paradas de todos los recorridos agrupadas por lugar
 * (`paraderosFisicos.json`, generado por scripts/generate_paraderos_fisicos.py).
 * El paradero más cercano se calcula aquí, en el teléfono: la ubicación no sale del navegador.
 */
import paraderosFisicos from '@/data/paradas/paraderosFisicos.json';
import { RECORRIDOS_BY_ID } from '@/data/recorridos/config';

/** Recorridos (ids del catálogo) que operan cada familia de paradas. */
const RECORRIDOS_DE_FAMILIA = {
  t1: ['t1a', 't1b'],
  t2: ['t2'],
  t3: ['t3'],
  t4: ['t4'],
  t5a: ['t5a'],
  t5b: ['t5b'],
};

/** @typedef {{ id: string; nombre: string; lat: number; lng: number; recorridos: object[] }} ParaderoFisico */

/** @type {ParaderoFisico[]} */
export const PARADEROS_FISICOS = paraderosFisicos.map((p) => {
  const ids = new Set(p.miembros.flatMap((m) => RECORRIDOS_DE_FAMILIA[m.familia] || []));
  return {
    id: p.id,
    nombre: p.nombre,
    lat: p.lat,
    lng: p.lng,
    recorridos: [...ids].map((id) => RECORRIDOS_BY_ID[id]).filter(Boolean),
  };
});

const POR_ID = Object.fromEntries(PARADEROS_FISICOS.map((p) => [p.id, p]));

/** @param {string | null | undefined} id */
export function getParaderoFisico(id) {
  return (id && POR_ID[id]) || null;
}

/** Distancia en metros entre dos coordenadas (haversine). */
export function distanciaM(lat1, lng1, lat2, lng2) {
  const rad = (g) => (g * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Paraderos más cercanos a una coordenada, del más próximo al más lejano.
 * @returns {(ParaderoFisico & { distanciaM: number })[]}
 */
export function paraderosMasCercanos(lat, lng, cantidad = 5) {
  return PARADEROS_FISICOS.map((p) => ({ ...p, distanciaM: distanciaM(lat, lng, p.lat, p.lng) }))
    .sort((a, b) => a.distanciaM - b.distanciaM)
    .slice(0, cantidad);
}

const sinTildes = (s) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** Paraderos cuyo nombre contiene el texto (sin distinguir tildes ni mayúsculas). */
export function buscarParaderos(texto, cantidad = 12) {
  const q = sinTildes((texto || '').trim());
  if (q.length < 2) return [];
  return PARADEROS_FISICOS.filter((p) => sinTildes(p.nombre).includes(q)).slice(0, cantidad);
}

/** «350 m» / «1,2 km». */
export function formatoDistancia(metros) {
  if (metros < 1000) return `${Math.round(metros / 10) * 10} m`;
  return `${(metros / 1000).toFixed(1).replace('.', ',')} km`;
}
